import { supabase } from '../lib/supabaseClient';
import type {
  CreateGoodsReceiptLineInput,
  CreatePurchaseOrderLineInput,
  CreatePurchaseRequestLineInput,
  GoodsReceipt,
  GoodsReceiptLine,
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseRequest,
  PurchaseRequestLine,
  UUID,
} from '../types/domain';

/**
 * Purchasing adapter: PR -> Approval -> PO -> GR -> (posting happens in
 * inventory.ts's stock ledger/balance reads, since posting a GR is what
 * creates the stock_ledger movement).
 *
 * Every write below calls a permission-gated RPC wrapper from migration
 * 0032 — never a direct INSERT/UPDATE. This mirrors exactly the flow
 * test13_rbac_vertical_slice1.sql exercises against Postgres directly; the
 * browser is just another (the real) caller of the same server functions.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */

// ---------------------------------------------------------------------------
// Purchase Requests
// ---------------------------------------------------------------------------

/** branchId, when passed, scopes the read to that branch's own PRs —
 * purchase_requests.branch_id is a real, non-null FK (every PR belongs to
 * exactly one branch), so this is a real filter, not a UI-only narrowing. */
export async function listPurchaseRequests(branchId?: UUID): Promise<PurchaseRequest[]> {
  let query = supabase
    .from('purchase_requests')
    .select('pr_id, tenant_id, branch_id, pr_number, status, requested_by, needed_date, notes, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listPurchaseRequests failed: ${error.message}`);
  return (data ?? []) as PurchaseRequest[];
}

export async function listPurchaseRequestLines(prId: UUID): Promise<PurchaseRequestLine[]> {
  const { data, error } = await supabase
    .from('purchase_request_lines')
    .select('pr_line_id, tenant_id, pr_id, item_id, uom_id, qty_requested, notes')
    .eq('pr_id', prId);
  if (error) throw new Error(`listPurchaseRequestLines failed: ${error.message}`);
  return (data ?? []) as PurchaseRequestLine[];
}

export interface CreatePurchaseRequestInput {
  tenantId: UUID;
  branchId: UUID;
  neededDate?: string | null;
  notes?: string | null;
  lines: CreatePurchaseRequestLineInput[];
}

/** Calls fn_create_purchase_request (migration 0032, permission-gated on purchase_request.create, branch-scoped). */
export async function createPurchaseRequest(input: CreatePurchaseRequestInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_purchase_request', {
    p_tenant_id: input.tenantId,
    p_branch_id: input.branchId,
    p_needed_date: input.neededDate ?? null,
    p_notes: input.notes ?? null,
    p_lines: input.lines,
  });
  if (error) throw new Error(`fn_create_purchase_request failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_approve_purchase_request (migration 0032, permission-gated on purchase_request.approve, branch-scoped). */
export async function approvePurchaseRequest(prId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_approve_purchase_request', { p_pr_id: prId });
  if (error) throw new Error(`fn_approve_purchase_request failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Purchase Orders
// ---------------------------------------------------------------------------

export async function listPurchaseOrders(branchId?: UUID): Promise<PurchaseOrder[]> {
  let query = supabase
    .from('purchase_orders')
    .select('po_id, tenant_id, branch_id, po_number, pr_id, supplier_id, status, issued_at, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listPurchaseOrders failed: ${error.message}`);
  return (data ?? []) as PurchaseOrder[];
}

export async function listPurchaseOrderLines(poId: UUID): Promise<PurchaseOrderLine[]> {
  const { data, error } = await supabase
    .from('purchase_order_lines')
    .select('po_line_id, tenant_id, po_id, pr_line_id, item_id, uom_id, qty_ordered, unit_price, qty_received')
    .eq('po_id', poId);
  if (error) throw new Error(`listPurchaseOrderLines failed: ${error.message}`);
  return (data ?? []) as PurchaseOrderLine[];
}

export interface CreatePurchaseOrderInput {
  prId: UUID;
  supplierId: UUID;
  lines: CreatePurchaseOrderLineInput[];
}

/** Calls fn_create_purchase_order_from_pr (migration 0032, permission-gated on purchase_order.create, branch-scoped). */
export async function createPurchaseOrderFromPr(input: CreatePurchaseOrderInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_purchase_order_from_pr', {
    p_pr_id: input.prId,
    p_supplier_id: input.supplierId,
    p_lines: input.lines,
  });
  if (error) throw new Error(`fn_create_purchase_order_from_pr failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// Goods Receipts
// ---------------------------------------------------------------------------

export async function listGoodsReceipts(branchId?: UUID): Promise<GoodsReceipt[]> {
  let query = supabase
    .from('goods_receipts')
    .select('gr_id, tenant_id, branch_id, gr_number, po_id, warehouse_id, status, received_by, posted_at, created_at')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listGoodsReceipts failed: ${error.message}`);
  return (data ?? []) as GoodsReceipt[];
}

export async function listGoodsReceiptLines(grId: UUID): Promise<GoodsReceiptLine[]> {
  const { data, error } = await supabase
    .from('goods_receipt_lines')
    .select('gr_line_id, tenant_id, gr_id, po_line_id, item_id, uom_id, qty_received')
    .eq('gr_id', grId);
  if (error) throw new Error(`listGoodsReceiptLines failed: ${error.message}`);
  return (data ?? []) as GoodsReceiptLine[];
}

export interface CreateGoodsReceiptInput {
  poId: UUID;
  warehouseId: UUID;
  lines: CreateGoodsReceiptLineInput[];
}

/** Calls fn_create_goods_receipt (migration 0032, permission-gated on goods_receipt.create, branch-scoped). Leaves the GR as DRAFT. */
export async function createGoodsReceipt(input: CreateGoodsReceiptInput): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_goods_receipt', {
    p_po_id: input.poId,
    p_warehouse_id: input.warehouseId,
    p_lines: input.lines,
  });
  if (error) throw new Error(`fn_create_goods_receipt failed: ${error.message}`);
  return data as UUID;
}

/**
 * Calls fn_post_goods_receipt_checked (migration 0032, permission-gated on
 * goods_receipt.post, branch-scoped) — the thin wrapper over the unmodified,
 * already-tested fn_post_goods_receipt (migration 0008). This is the single
 * action in the whole vertical slice that actually writes to stock_ledger.
 */
export async function postGoodsReceipt(grId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_post_goods_receipt_checked', { p_gr_id: grId });
  if (error) throw new Error(`fn_post_goods_receipt_checked failed: ${error.message}`);
}
